/**
 * The inspector's field primitives (DESIGN §9.3).
 *
 * Each one is bound to the spec's own range, so an input cannot produce a
 * document the validator would refuse — `min`/`max` come from `RANGES`, the
 * selects from the catalogues, and the colour input is paired with its hex so
 * a customer can paste a brand colour rather than hunt for it in a picker.
 */

import type { ReactNode } from "react";

import { COLOR_RE } from "./spec";

/**
 * A run of fields under a quiet heading — *Content*, *Style*.
 *
 * A block's inspector is a dozen inputs in one column, and without these it
 * reads as one long form where the label of the field you want is the only
 * thing to aim at. The heading is the console's own small-caps label, so it
 * groups without competing with the panel's title.
 */
export function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="design-group">
      <h5 className="design-group-label">{label}</h5>
      {children}
    </section>
  );
}

/**
 * One labelled control. `error` is the validator's own sentence about the
 * value in it (MODULES §7.3): a generated params form is the one place in the
 * inspector where a customer types something the *server* has rules about, so
 * the field says what is wrong with it rather than only the sidebar.
 */
export function Field({
  label,
  hint,
  error,
  required,
  requiredTitle,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** The word behind the `*`, translated by the caller. */
  requiredTitle?: string;
  children: ReactNode;
}) {
  return (
    <label className="design-field">
      <span className="design-field-label">
        {label}
        {required && (
          <span className="error-text" title={requiredTitle}>
            {" *"}
          </span>
        )}
      </span>
      {children}
      {error && <span className="error-text small">{error}</span>}
      {hint && <span className="muted small">{hint}</span>}
    </label>
  );
}

export function TextField({
  label,
  value,
  max,
  multiline,
  onChange,
}: {
  label: string;
  value: string;
  max: number;
  multiline?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} hint={`${value.length}/${max}`}>
      {multiline ? (
        <textarea
          value={value}
          maxLength={max}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input value={value} maxLength={max} onChange={(event) => onChange(event.target.value)} />
      )}
    </Field>
  );
}

export function NumberField({
  label,
  value,
  range,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  range: readonly [number, number];
  unit?: string;
  onChange: (value: number) => void;
}) {
  return (
    <Field label={unit ? `${label} (${unit})` : label}>
      <input
        type="number"
        value={value}
        min={range[0]}
        max={range[1]}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  labelFor,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labelFor: (option: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <Field label={label}>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {labelFor(option)}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * A choice shown as a row of buttons rather than folded into a select
 * (DESIGN §9.3).
 *
 * This is where a block is *placed*, so it is the first thing in the inspector
 * and it says what it is set to without being opened — a select that has to be
 * clicked to reveal "Centre" is the wrong control for the question "why is
 * this in the middle?".
 */
export function SegmentedField<T extends string>({
  label,
  value,
  options,
  labelFor,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labelFor: (option: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <div className="design-field design-segmented">
      <span className="design-field-label">{label}</span>
      <div className="design-segments" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={option === value ? "on" : undefined}
            aria-pressed={option === value}
            onClick={() => onChange(option)}
          >
            {labelFor(option)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ToggleField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="design-field design-toggle">
      <input type="checkbox" checked={value} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/**
 * A swatch and the hex beside it. `<input type="color">` cannot express alpha,
 * so the text field is the one that accepts `#RRGGBBAA` — and it is also how a
 * brand colour gets pasted in rather than eyeballed.
 */
export function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const valid = COLOR_RE.test(value);
  return (
    <Field label={label}>
      <span className="design-color">
        <input
          type="color"
          aria-label={label}
          value={valid ? value.slice(0, 7) : "#000000"}
          onChange={(event) => onChange(event.target.value.toUpperCase() + value.slice(7))}
        />
        <input
          className={valid ? "mono" : "mono design-invalid"}
          value={value}
          maxLength={9}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
    </Field>
  );
}
